import { get, isEmpty, merge } from "lodash";
import { PlotData, Layout } from "plotly.js";
import { doHidden, COLORWAY } from "../utils";
import { IModel, IRawPlotlyChart, IResult, IAction } from "../types";
import { IOutputProps } from "./types";
import "./style.scss";
import { useMemo } from "react";
import ArchiveIcon from "./ArchiveIcon";
import { downloadData } from "./download";
import { applyFunction } from "../action";
import { ChartLoadingSkeleton } from "./loading";
import { isTemplateRefreshing } from "../dependencyIndex";
import RefreshingState from "../RefreshingState";
import AutoSizedPlot from "./AutoSizedPlot";

export interface IRawPlotlyChartProps extends IOutputProps {
  template: IRawPlotlyChart;
}

const transformData = (template: IRawPlotlyChart, model: IModel, result: IResult): Array<Partial<PlotData>> => {
  const plotDataArray =
    (
      template.dataName ?
        get(result, template.dataName, []) :
        template.data ?
          applyFunction(model, template.data, undefined, result) ?? [] :
          []
    ) as Array<Partial<PlotData>>;

  return plotDataArray.map(plotData => {
    const newPlotData = { ...plotData };
    if (Number.isInteger(plotData.line?.color))
      newPlotData.line = { ...plotData.line, color: COLORWAY[plotData.line?.color as number] }
    if (plotData.marker && newPlotData.marker) {
      if (Number.isInteger(plotData.marker.color))
        newPlotData.marker = { ...plotData.marker, color: COLORWAY[plotData.marker?.color as number] }
      if (Number.isInteger(plotData.marker.line?.color))
        newPlotData.marker.line = { ...plotData.marker.line, width: 2, color: COLORWAY[plotData.marker.line?.color as number] }
    }
    return newPlotData;
  })
};

const createLayout = (template: IRawPlotlyChart, model: IModel, result: IResult): Partial<Layout> => (
  template.layoutName ?
    get(result, template.layoutName, {}) as Partial<Layout> :
    template.layout ?
      (
        (template.layout as IAction).sources ?
          applyFunction(model, template.layout as IAction, undefined, result) :
          template.layout
      ) as Partial<Layout> :
      {}
);

const RawPlotlyChart = (props: IRawPlotlyChartProps) => {
  const { model, template, result, metadata } = props;
  const data = useMemo(() => transformData(template, model, result), [template, model, result]);
  const layout = useMemo(() => createLayout(template, model, result), [template, model, result]);
  const containerClassName = template.height ? "chart-container" : "small-chart-container";
  const refreshing = isTemplateRefreshing(template, metadata, props.mutators.getPath);
  const skeletonVariant = useMemo(
    () => (data.some((trace) => trace.type === "sankey") ? "sankey" : "plot"),
    [data]
  );

  if (doHidden(props)) {
    return null;
  }

  if (isEmpty(data) && refreshing) {
    return (
      <ChartLoadingSkeleton
        className={containerClassName}
        height={template.height}
        label={template.label || template.name}
        variant={skeletonVariant}
      />
    );
  }

  if (isEmpty(data)) {
    return null;
  }

  return (
    <RefreshingState refreshing={refreshing}>
    <div
      className={containerClassName}
      {...(template.height && { style: { height: `${template.height}px` } })}
    >
      <AutoSizedPlot
        chartId={template.name}
        data={data}
        layout={merge({ colorway: COLORWAY }, layout)}
        config={{
          displaylogo: false,
          modeBarButtonsToRemove: ["select2d", "lasso2d", "autoScale2d"],
          modeBarButtonsToAdd: [
            {
              name: "downloadData",
              title: "Download data as a csv",
              icon: ArchiveIcon,
              click: () => downloadData(template, data),
            },
          ],
        }}
      />
    </div>
    </RefreshingState>
  );
};

export default RawPlotlyChart;
